/**
 * `/s/<token>` — what a recipient with no account sees.
 *
 * The read-only twin of `PublicFormPage.jsx`, and like it this renders for
 * somebody who has no session and must never be offered one: there is
 * deliberately no route from this screen into the signed-in app, no login
 * prompt, and no branding of ours competing with the org's own name.
 *
 * Three things about it are decisions rather than styling:
 *
 * **It leads with the picture.** The whole reason this feature exists is that
 * a shortlist you send someone is looked at, not read — so the layout is the
 * `gallery` view's card grid rather than a table, and a row with no image
 * still renders as a card (the same call `GalleryView.jsx` makes, for the same
 * reason: hiding it would silently disagree with what the sender thought they
 * shared).
 *
 * **Every value is rendered as TEXT.** `title` is written by the issuer and
 * the field values come out of the org's own rows, but neither is trusted as
 * markup — React escapes by default and nothing here reaches for
 * `dangerouslySetInnerHTML`. A shared page is exactly where a stored-XSS
 * payload would want to land, since its audience is outside the org.
 *
 * **It says when the link expires.** A recipient who bookmarks it should learn
 * that from the page rather than from it silently breaking later.
 */
import { useState, useEffect, useMemo } from 'react';
import { ImageOff, Clock, AlertCircle } from 'lucide-react';
import { getPublicShare, getPublicShareFileUrls } from '../core/api';
import { fmtZoned } from '../core/utils';

const FILE_TYPES = ['file', 'image'];

function fmtValue(value, spec) {
  if (value === null || value === undefined || value === '') return null;
  if (spec?.type === 'boolean') return value ? 'Yes' : 'No';
  if (spec?.type === 'money' || (spec?.type === 'number' && spec?.format)) {
    const f = spec.format || {};
    try {
      return new Intl.NumberFormat(undefined, {
        style: f.currency ? 'currency' : 'decimal',
        currency: f.currency || undefined,
        maximumFractionDigits: f.decimals ?? (spec.type === 'money' ? 2 : 0),
      }).format(Number(value));
    } catch { /* fall through to the plain string below */ }
  }
  if (spec?.type === 'datetime') {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) {
      return spec.include_time ? fmtZoned(d) : d.toLocaleDateString();
    }
  }
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

function fmtExpiry(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

function ShareCard({ row, fields, imageField, src, childStrip }) {
  // The first non-image field carries the card's heading; the rest are the
  // caption. No config says which is the title, so this is derived from the
  // order the ISSUER picked the fields in, which is the only signal there is.
  const textFields = fields.filter((f) => !FILE_TYPES.includes(f.type));
  const [head, ...rest] = textFields;
  const headValue = head ? fmtValue(row[`${head.key}__label`] ?? row[head.key], head) : null;

  return (
    <article className="rounded-xl overflow-hidden border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
      {imageField && (
        <div className="aspect-4/3 w-full max-w-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center overflow-hidden">
          {src ? (
            <img src={src} alt={headValue || ''} loading="lazy"
                 className="h-full w-full object-cover" />
          ) : (
            <ImageOff size={20} className="text-neutral-400 dark:text-neutral-600" aria-hidden="true" />
          )}
        </div>
      )}
      <div className="p-3 space-y-2">
        {head && (
          <h2 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
            {headValue || <span className="font-normal text-neutral-400">—</span>}
          </h2>
        )}
        <dl className="space-y-1">
          {rest.map((f) => {
            const v = fmtValue(row[`${f.key}__label`] ?? row[f.key], f);
            if (v === null) return null;
            return (
              <div key={f.key} className="flex gap-2 text-xs">
                <dt className="shrink-0 text-neutral-500 dark:text-neutral-400">{f.label || f.key}</dt>
                <dd className="min-w-0 text-neutral-800 dark:text-neutral-200 wrap-break-word">{v}</dd>
              </div>
            );
          })}
        </dl>
        {childStrip}
      </div>
    </article>
  );
}

export function PublicSharePage({ token }) {
  const [state, setState] = useState({ status: 'loading' });
  const [fileUrls, setFileUrls] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getPublicShare(token);
        if (!cancelled) setState({ status: 'ready', data });
      } catch (e) {
        if (!cancelled) setState({ status: 'gone', message: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const data = state.data;
  const imageField = useMemo(
    () => (data?.fields || []).find((f) => FILE_TYPES.includes(f.type)) || null,
    [data],
  );
  const childImageField = useMemo(
    () => (data?.child?.fields || []).find((f) => FILE_TYPES.includes(f.type)) || null,
    [data],
  );

  // One batched call for every picture on the page, the same shape
  // GalleryView's own signed-URL fetch takes. A `file` field needs it; an
  // `image` field already holds something renderable and is passed through.
  useEffect(() => {
    if (!data) return undefined;
    const collected = [];
    if (imageField?.type === 'file') {
      collected.push(...(data.rows || []).map((r) => r[imageField.key]));
    }
    if (childImageField?.type === 'file') {
      // The child photos are the point of including a child at all, so they
      // ride the SAME batched call rather than a second round trip.
      for (const group of Object.values(data.child?.rows || {})) {
        collected.push(...group.map((r) => r[childImageField.key]));
      }
    }
    const keys = [...new Set(collected.filter((k) => typeof k === 'string' && k))];
    if (keys.length === 0) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await getPublicShareFileUrls(token, keys);
        if (!cancelled) setFileUrls(res.urls || {});
      } catch {
        // The records are still worth showing — degrade to placeholder tiles
        // rather than an error page, same posture GalleryView takes.
      }
    })();
    return () => { cancelled = true; };
  }, [token, data, imageField, childImageField]);

  if (state.status === 'loading') {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950 flex items-center justify-center">
        <p className="text-sm text-neutral-500">Loading…</p>
      </div>
    );
  }

  if (state.status === 'gone') {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950 flex items-center justify-center px-4">
        <div className="max-w-sm text-center space-y-3">
          <AlertCircle size={28} className="mx-auto text-neutral-400" aria-hidden="true" />
          {/* The server's one neutral message, verbatim. It must not be
              elaborated into "expired" or "revoked" here — which terminal
              state a link is in is not a recipient's business, and inventing
              a reason on the client would undo that on the one screen it
              matters. */}
          <p className="text-sm text-neutral-700 dark:text-neutral-300">{state.message}</p>
        </div>
      </div>
    );
  }

  const expiry = fmtExpiry(data.expires_at);
  const childSrc = (row) => {
    if (!childImageField) return null;
    const raw = row[childImageField.key];
    if (!raw) return null;
    return childImageField.type === 'file' ? fileUrls[raw] || null : String(raw);
  };
  const stripFor = (parentId) => {
    const group = data.child?.rows?.[String(parentId)] || [];
    if (group.length === 0) return null;
    const captionField = (data.child.fields || []).find((f) => !FILE_TYPES.includes(f.type));
    return (
      <div className="pt-1">
        {/* A horizontal strip rather than a second grid: the child is
            supporting detail for ITS card, and a grid inside a grid stops
            reading as one record. Its own overflow container, so a long photo
            set scrolls sideways instead of widening the page. */}
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {group.map((c) => {
            const src = childSrc(c);
            const cap = captionField ? fmtValue(c[captionField.key], captionField) : null;
            return (
              <div key={c.id} className="h-14 w-20 shrink-0 overflow-hidden rounded-sm bg-neutral-100 dark:bg-neutral-800">
                {src ? (
                  <img src={src} alt={cap || ''} loading="lazy"
                       className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center px-1 text-center text-[9px] leading-tight text-neutral-500 dark:text-neutral-400">
                    {cap || <ImageOff size={12} aria-hidden="true" />}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  const srcFor = (row) => {
    if (!imageField) return null;
    const raw = row[imageField.key];
    if (!raw) return null;
    return imageField.type === 'file' ? fileUrls[raw] || null : String(raw);
  };

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950">
      <div className="mx-auto max-w-5xl px-4 py-8 space-y-5">
        <header className="space-y-1">
          {/* The org's own name, from the server — so the page cannot be
              re-skinned as somebody else. Same answer to impersonation the
              public form page gives. */}
          <p className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
            {data.org_name}
          </p>
          <h1 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
            {data.title || `${data.entity_label} — shared with you`}
          </h1>
          {expiry && (
            <p className="flex items-center gap-1.5 text-xs text-neutral-500 dark:text-neutral-400">
              <Clock size={12} aria-hidden="true" /> This link works until {expiry}.
            </p>
          )}
        </header>

        {(data.rows || []).length === 0 ? (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            There&rsquo;s nothing here to show. Whoever sent this link can send a new one.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.rows.map((row) => (
              <ShareCard key={row.id} row={row} fields={data.fields}
                         imageField={imageField} src={srcFor(row)}
                         childStrip={stripFor(row.id)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
